import React, { FC } from 'react';
import { Text, RichText } from '@sitecore-jss/sitecore-jss-react';
import { THeroSectionTextProps } from '../../types/heroSectionText.types';
import { HeroSectionTextModel } from '../../models/heroSectionText.model';
import CTAButton, { ctaEnums } from '../../../@shared/ctaButton';
import RoutableNotEditableLink from '../../../@shared/routableNotEditableLink';
import { targetLinkdsk } from '../../../../utils/targetDskLink';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import InfoToolTip from '../../../@shared/infoToolTip';
import Icon from '../../../@shared/icon';
import { googleClickCtaEvent } from '../../../../utils/analytics';
import useGetBuildAndPriceLink from '../../../../hooks/useGetBuildAndPriceLink';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import { appendRootToInternalLink } from '../../../../utils/appendRootToInternalLink.utils';

const HeroSectionText: FC<THeroSectionTextProps> = (props) => {
  const model = new HeroSectionTextModel(props);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const buildAndPriceUrl = useGetBuildAndPriceLink('model');

  return (
    <div className="product-hero-section-text">
      <Text field={model.eyebrow} tag="p" className="product-hero-section-text__eyebrow" editable={false} />
      <Text field={model.title} tag="h1" className="product-hero-section-text__title" editable={false} />
      <div className="product-hero-section-text__starting-price">
        <Text field={model.optionalLabel} tag="p" className="product-hero-section-text__starting-price--label" editable={false} />
        <RichText field={model.msrp} tag="div" className="product-hero-section-text__starting-price--text rich-text" editable={false} />
        <InfoToolTip showIcon={model.showInfoTooltip} width={300}>
          <div className="product-hero-section-text__tooltip__wrapper">
            <Text field={model.infoTooltipTitle} tag="h2" className="product-hero-section-text__tooltip__title" editable={false} />
            <Text field={model.infoTooltipDescription} tag="div" className="product-hero-section-text__tooltip__body" editable={false} />
          </div>
        </InfoToolTip>
      </div>

      <RichText field={model.description} tag="div" className="product-hero-section-text__description rich-text" editable={false} />
      {
        model.enableBuildPriceCTA && buildAndPriceUrl && (
          <CTAButton type={ctaEnums.primaryRed}>
            <RoutableNotEditableLink
              className="product-hero-section-text__link product-hero-section-text__link--primary product-hero-section-text__build-price"
              href={appendRootToInternalLink(buildAndPriceUrl)}
            >
              <DictionaryTerm keyword={'cnhi-build-price'} />
            </RoutableNotEditableLink>
          </CTAButton>
        )
      }
      {model?.primaryLink?.label && model?.primaryLink?.url && (
        <CTAButton type={ctaEnums.primaryRed}>
          <RoutableNotEditableLink
            className="product-hero-section-text__link product-hero-section-text__link--primary"
            href={model.primaryLinkUrl}
            target={targetLinkdsk({ value: { href: model?.primaryLink?.url, target: model?.primaryLink?.target } }, isDesktop)}
            onClick={() => googleClickCtaEvent('Hero', model?.primaryLink?.label, model?.title?.value ?? model?.eyebrow?.value)}
          >
            {model.primaryLink.label}
          </RoutableNotEditableLink>
        </CTAButton>
      )}
      {model?.secondaryLink?.label && model?.secondaryLink?.url && (
        <span className={'product-hero-section-text__link product-hero-section-text__link--secondary'}>
          <RoutableNotEditableLink
            href={model.secondaryLinkUrl}
            target={targetLinkdsk({ value: { href: model?.secondaryLink?.url, target: model?.secondaryLink?.target } }, isDesktop)}
            onClick={() => googleClickCtaEvent('Hero', model?.secondaryLink?.label, model?.title?.value ?? model?.eyebrow?.value)}
          >
            {model.secondaryLink.label}
          </RoutableNotEditableLink>
          <Icon iconName="arrow-simple-right" size={24} extraClassName="product-hero-section-text__icon" color="primary" />
        </span>
      )}
      <div className="product-hero-section-text__quick-spec-wrapper">
        {model?.summarySpecs &&
          model?.summarySpecs?.map((spec) => (
            <div className="product-hero-section-text__quick-spec-item">
              <RichText
                field={spec.key}
                tag="div"
                className="product-hero-section-text__quick-spec-item--title rich-text"
                editable={false}
              />
              <RichText
                field={spec.value}
                tag="div"
                className="product-hero-section-text__quick-spec-item--value rich-text"
                editable={false}
              />
            </div>
          ))}
      </div>
    </div>
  );
};

export default HeroSectionText;
